import type { GridValidRowModel } from "@mui/x-data-grid-premium";
import type { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import type { GridStateColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import type { MutableRefObject } from "react";
import { logger } from "src/logger";

const DELIMITER = ";";
const LINE_BREAK = "\r\n";

/**
 * Без BOM Excel читает UTF-8 как latin1 и ломает умляуты.
 */
const UTF8_BOM = "\uFEFF";

/**
 * Excel исполняет ячейку, начинающуюся с этих символов, как формулу.
 */
const FORMULA_CHARS = ["=", "+", "-", "@", "\t", "\r"];

const NEGATIVE_NUMBER = /^-[\d.,]+$/;

const PLAIN_DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * Служебные колонки грида: чекбоксы, раскрытие строк, кнопки действий.
 */
const isServiceColumn = (column: GridStateColDef) =>
  column.field.startsWith("__") || column.type === "actions";

export type CsvColumn<Row extends GridValidRowModel = GridValidRowModel> = {
  column: GridStateColDef<Row>;
  /**
   * Тип из конфига списка: числовые колонки грид видит строковыми
   * (см. convertDataGridColumns), а в файле им нужна немецкая запятая.
   */
  isNumber: boolean;
};

/**
 * Видимые колонки грида в том порядке, в каком их расставил пользователь.
 */
export const getCsvColumns = <Row extends GridValidRowModel>(
  visibleColumns: GridStateColDef<Row>[],
  numberFields: Set<string>,
): CsvColumn<Row>[] =>
  visibleColumns
    .filter((column) => !isServiceColumn(column))
    .map((column) => ({
      column,
      isNumber: numberFields.has(column.field),
    }));

/**
 * Значение ячейки так, как его показывает грид: valueGetter, затем valueFormatter.
 * Колонки, у которых есть только renderCell, отдают сырое значение поля.
 */
const getCellValue = <Row extends GridValidRowModel>(
  row: Row,
  { column }: CsvColumn<Row>,
  apiRef: MutableRefObject<GridApiPremium>,
): { value: unknown; formatted: unknown } => {
  const rawValue = row[column.field];
  const value = column.valueGetter
    ? column.valueGetter(rawValue as never, row, column, apiRef)
    : rawValue;

  return {
    value,
    formatted: column.valueFormatter
      ? column.valueFormatter(value as never, row, column, apiRef)
      : value,
  };
};

const stringifyValue = <Row extends GridValidRowModel>(
  value: unknown,
  sourceValue: unknown,
  csvColumn: CsvColumn<Row>,
  labels: { yes: string; no: string },
): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "boolean") {
    return value ? labels.yes : labels.no;
  }
  if (typeof value === "number") {
    return String(value).replace(".", ",");
  }
  if (value instanceof Date) {
    return value.toLocaleDateString("de-DE");
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => stringifyValue(item, item, csvColumn, labels))
      .filter((item) => item !== "")
      .join(", ");
  }
  if (typeof value === "object") {
    // Колонка рисует объект компонентом и не говорит, какое поле выгружать:
    // ей нужен valueGetter. Ячейка остаётся пустой, файл не ломается.
    logger.warn(
      `CSV export: column "${csvColumn.column.field}" resolved to an object, add a valueGetter`,
    );
    return "";
  }

  const text = String(value);

  // Число с точкой — как его отдал API ("150.000000") или toFixed() форматтера:
  // Excel в немецкой локали прочитает его текстом. Сверка с исходным значением
  // отсекает немецкие тысячи: "1.234" из 1234 — уже отформатированное число.
  if (
    csvColumn.isNumber &&
    PLAIN_DECIMAL.test(text) &&
    Number(text) === Number(sourceValue)
  ) {
    return String(Number(text)).replace(".", ",");
  }

  return text;
};

const cellText = <Row extends GridValidRowModel>(
  { value, formatted }: { value: unknown; formatted: unknown },
  csvColumn: CsvColumn<Row>,
  labels: { yes: string; no: string },
) => stringifyValue(formatted, value, csvColumn, labels);

const escapeFormula = (value: string): string => {
  if (value === "" || !FORMULA_CHARS.includes(value[0])) {
    return value;
  }

  // Немецкое -1234,50 должно остаться числом для Excel.
  return NEGATIVE_NUMBER.test(value) ? value : `'${value}`;
};

const quote = (value: string): string => {
  const singleLine = value.replace(/\r\n|\r|\n/g, " ");

  if (!/[";]/.test(singleLine)) {
    return singleLine;
  }

  return `"${singleLine.replace(/"/g, '""')}"`;
};

const toCsvCell = (value: string) => quote(escapeFormula(value));

export const buildCsv = <Row extends GridValidRowModel>({
  rows,
  columns,
  apiRef,
  labels,
}: {
  rows: Row[];
  columns: CsvColumn<Row>[];
  apiRef: MutableRefObject<GridApiPremium>;
  labels: { yes: string; no: string };
}): string => {
  const header = columns
    .map(({ column }) => toCsvCell(column.headerName ?? column.field))
    .join(DELIMITER);

  const lines = rows.map((row) =>
    columns
      .map((csvColumn) =>
        toCsvCell(
          cellText(getCellValue(row, csvColumn, apiRef), csvColumn, labels),
        ),
      )
      .join(DELIMITER),
  );

  return UTF8_BOM + [header, ...lines].join(LINE_BREAK) + LINE_BREAK;
};
